/**
 * COMPENSO DEI PARTNER — quanto matura un partner sugli abbonamenti delle aziende che ha portato a YumaOS.
 *
 * Doc canonico: `docs/01-ARCHITECTURE/PROGRAMMA-PARTNER-MODEL.md`.
 *
 * # Regole di business decise da Simone il 19/10/2026, scritte qui perché valgono soldi
 *
 *  - il compenso si calcola SOLO sui posti (Base, Pro, Business), su quanto l'azienda paga davvero: al netto degli
 *    sconti, IVA esclusa. Consumi e moduli a parte restano fuori (sono costo del fornitore × ricarico);
 *  - matura solo su incassi confermati; un rimborso è uno storno in proporzione;
 *  - la percentuale dipende dal MESE DI ABBONAMENTO dell'azienda, contato dal suo primo incasso (`abbonata_dal`), a
 *    scaglioni scritti nel profilo del partner. Profilo di Romano Paolicelli: 10% nei mesi 1-12, 7,5% nei mesi 13-24,
 *    5% dal 25° finché resta abbonata;
 *  - il profilo si COPIA sull'azienda quando le viene abbinato il partner: cambiare il profilo del partner non tocca le
 *    aziende già portate;
 *  - fisco e previdenza (ritenuta, Enasarco, IVA) sono il REGIME del partner, un dato: «non per forza sarà coinvolta
 *    l'Enasarco» (Simone). Nessuna regola italiana è scritta qui;
 *  - si paga ogni N mesi (predefinito 3, ogni partner può averne uno suo), a periodi allineati all'anno solare.
 *
 * # Perché il mese si conta a giorni
 *
 * Una fattura annuale, o una mensile che a metà strada diventa annuale, copre più mesi di abbonamento, e a volte due
 * scaglioni (mesi 7-18: metà al 10%, metà al 7,5%). Ogni GIORNO del periodo pagato prende la percentuale del suo mese di
 * abbonamento, e la riga prende la media: nessun caso speciale per mensile, annuale o rateo di un cambio di posti.
 *
 * # Date e importi
 *
 * Date come `AAAA-MM-GG` in UTC: vengono da Stripe e servono solo a contare mesi e giorni. Importi in CENTESIMI interi,
 * con la valuta accanto in chi li salva. Niente Firestore e niente i18n qui: lo stesso file lo eseguono il server, che
 * SCRIVE i movimenti, e le pagine, che li mostrano. Una sola aritmetica.
 *
 * @since 2026-10-19
 */

// ============================================================================
// PROFILO DI COMPENSO
// ============================================================================

/** Uno scaglione: dal mese di abbonamento `dal_mese` al mese `al_mese` compreso (`null` = per sempre). */
export interface ScaglioneCompenso {
  dal_mese: number;
  al_mese: number | null;
  /** Percentuale sui posti, es. 7.5. */
  percentuale: number;
}

/** I campi di business di un profilo: quelli che decidono il conto. */
export interface ProfiloCompensoDati {
  nome: string;
  /** Su cosa si calcola. Oggi esistono solo i posti: il tipo lo dichiara perché il dato lo porta scritto. */
  base: 'posti';
  scaglioni: ScaglioneCompenso[];
}

/** Il profilo come lo ha deciso Simone per Romano Paolicelli (19/10/2026): l'esempio delle prove e della console. */
export const SCAGLIONI_ESEMPIO: readonly ScaglioneCompenso[] = [
  { dal_mese: 1, al_mese: 12, percentuale: 10 },
  { dal_mese: 13, al_mese: 24, percentuale: 7.5 },
  { dal_mese: 25, al_mese: null, percentuale: 5 },
];

/** Perché un profilo non va: codici stabili (la pagina li traduce), con lo scaglione coinvolto. */
export type ProblemaProfilo =
  | { codice: 'nome_mancante' }
  | { codice: 'base_sconosciuta' }
  | { codice: 'nessuno_scaglione' }
  | { codice: 'non_parte_dal_primo_mese' }
  | { codice: 'mese_non_valido'; indice: number }
  | { codice: 'percentuale_non_valida'; indice: number }
  | { codice: 'buco_o_sovrapposizione'; indice: number }
  | { codice: 'scaglione_dopo_per_sempre'; indice: number };

/**
 * Un profilo è valido se gli scaglioni partono dal mese 1 e si susseguono senza buchi né sovrapposizioni. L'ultimo
 * può finire (dopo, il compenso è zero) o durare per sempre (`al_mese: null`).
 */
export function validaProfilo(profilo: ProfiloCompensoDati): ProblemaProfilo[] {
  const problemi: ProblemaProfilo[] = [];
  if (!profilo.nome?.trim()) problemi.push({ codice: 'nome_mancante' });
  if (profilo.base !== 'posti') problemi.push({ codice: 'base_sconosciuta' });
  const scaglioni = profilo.scaglioni ?? [];
  if (scaglioni.length === 0) {
    problemi.push({ codice: 'nessuno_scaglione' });
    return problemi;
  }
  if (scaglioni[0].dal_mese !== 1) problemi.push({ codice: 'non_parte_dal_primo_mese' });
  scaglioni.forEach((s, indice) => {
    const meseIntero = (n: number) => Number.isInteger(n) && n >= 1;
    if (!meseIntero(s.dal_mese) || (s.al_mese !== null && (!meseIntero(s.al_mese) || s.al_mese < s.dal_mese))) {
      problemi.push({ codice: 'mese_non_valido', indice });
    }
    if (!Number.isFinite(s.percentuale) || s.percentuale < 0 || s.percentuale > 100) {
      problemi.push({ codice: 'percentuale_non_valida', indice });
    }
    if (indice > 0) {
      const prima = scaglioni[indice - 1];
      if (prima.al_mese === null) problemi.push({ codice: 'scaglione_dopo_per_sempre', indice });
      else if (s.dal_mese !== prima.al_mese + 1) problemi.push({ codice: 'buco_o_sovrapposizione', indice });
    }
  });
  return problemi;
}

/** La percentuale di un mese di abbonamento (1 = il primo). Fuori da ogni scaglione: zero. */
export function percentualeDelMese(scaglioni: readonly ScaglioneCompenso[], mese: number): number {
  const s = scaglioni.find((x) => mese >= x.dal_mese && (x.al_mese === null || mese <= x.al_mese));
  return s ? s.percentuale : 0;
}

// ============================================================================
// DATE (AAAA-MM-GG, UTC)
// ============================================================================

const GIORNO_MS = 24 * 60 * 60 * 1000;
const FORMA_DATA = /^\d{4}-\d{2}-\d{2}$/;

function msDellaData(data: string): number {
  if (!FORMA_DATA.test(data)) throw new Error(`Data non valida: «${data}» (serve AAAA-MM-GG)`);
  const [a, m, g] = data.split('-').map(Number);
  const ms = Date.UTC(a, m - 1, g);
  if (new Date(ms).getUTCDate() !== g) throw new Error(`Data inesistente: «${data}»`);
  return ms;
}

function dataDaMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** La data (UTC) di un istante in secondi, come li dà Stripe. */
export function dataDaSecondi(secondi: number): string {
  return dataDaMs(secondi * 1000);
}

/** `data` + `n` mesi di calendario; se il giorno non esiste (31 → febbraio) si ferma all'ultimo del mese. */
export function aggiungiMesi(data: string, n: number): string {
  const [a, m, g] = data.split('-').map(Number);
  const indice = a * 12 + (m - 1) + n;
  const anno = Math.floor(indice / 12);
  const mese = indice - anno * 12;
  const ultimo = new Date(Date.UTC(anno, mese + 1, 0)).getUTCDate();
  return dataDaMs(Date.UTC(anno, mese, Math.min(g, ultimo)));
}

/**
 * Il mese di abbonamento in cui cade `giorno` per un'azienda abbonata da `abbonataDal`: il mese n va da
 * `abbonataDal + (n-1) mesi` incluso a `abbonataDal + n mesi` escluso. Prima dell'inizio: 0 (nessun compenso).
 */
export function meseDiAbbonamento(abbonataDal: string, giorno: string): number {
  const inizio = msDellaData(abbonataDal);
  const g = msDellaData(giorno);
  if (g < inizio) return 0;
  const [a1, m1] = abbonataDal.split('-').map(Number);
  const [a2, m2] = giorno.split('-').map(Number);
  let n = (a2 - a1) * 12 + (m2 - m1) + 1;
  while (n > 1 && msDellaData(aggiungiMesi(abbonataDal, n - 1)) > g) n -= 1;
  while (msDellaData(aggiungiMesi(abbonataDal, n)) <= g) n += 1;
  return n;
}

// ============================================================================
// PROVVIGIONE DI UNA RIGA DI FATTURA
// ============================================================================

/** Una riga di posti di una fattura pagata, già ridotta a ciò che conta (la prepara il server da Stripe). */
export interface RigaDiPosti {
  /** Id della riga su Stripe: insieme all'id della fattura fa l'id del movimento (niente doppioni). */
  id_riga: string;
  /** Importo pagato per la riga: al netto degli sconti, IVA esclusa. Negativo per un rateo a credito. */
  importo_centesimi: number;
  /** Periodo di servizio pagato dalla riga: dal giorno `periodo_dal` incluso al giorno `periodo_al` escluso. */
  periodo_dal: string;
  periodo_al: string;
}

export interface ProvvigioneDellaRiga {
  base_centesimi: number;
  /** Media delle percentuali dei giorni pagati: è ciò che si applica alla base. */
  percentuale: number;
  importo_centesimi: number;
  /** Mesi di abbonamento coperti dalla riga (il primo e l'ultimo), per mostrarli al partner. */
  dal_mese: number;
  al_mese: number;
}

/** Arrotondamento al centesimo, simmetrico per gli importi negativi (uno storno vale quanto la provvigione). */
export function arrotondaCentesimi(valore: number): number {
  return Math.sign(valore) * Math.round(Math.abs(valore));
}

/**
 * La provvigione di una riga di posti. Ogni giorno del periodo pagato prende la percentuale del suo mese di
 * abbonamento; la riga prende la media. Un periodo vuoto (rateo di un istante) prende la percentuale del suo giorno.
 */
export function provvigioneDellaRiga(
  scaglioni: readonly ScaglioneCompenso[],
  abbonataDal: string,
  riga: RigaDiPosti,
): ProvvigioneDellaRiga {
  const dal = msDellaData(riga.periodo_dal);
  const al = Math.max(msDellaData(riga.periodo_al), dal + GIORNO_MS);
  let somma = 0;
  let giorni = 0;
  let primo = 0;
  let ultimo = 0;
  for (let ms = dal; ms < al; ms += GIORNO_MS) {
    const mese = meseDiAbbonamento(abbonataDal, dataDaMs(ms));
    if (giorni === 0) primo = mese;
    ultimo = mese;
    somma += mese > 0 ? percentualeDelMese(scaglioni, mese) : 0;
    giorni += 1;
  }
  const percentuale = somma / giorni;
  return {
    base_centesimi: riga.importo_centesimi,
    percentuale: Math.round(percentuale * 10000) / 10000,
    importo_centesimi: arrotondaCentesimi((riga.importo_centesimi * percentuale) / 100),
    dal_mese: primo,
    al_mese: ultimo,
  };
}

/**
 * Lo storno di un rimborso: la stessa frazione delle provvigioni di quella fattura. `rimborsato` e `totale` sono gli
 * importi del pagamento su Stripe (IVA compresa: conta il rapporto, non la cifra). Torna un numero ≤ 0.
 */
export function stornoDelRimborso(provvigioniDellaFattura: number, rimborsatoCentesimi: number, totaleCentesimi: number): number {
  if (totaleCentesimi <= 0 || rimborsatoCentesimi <= 0) return 0;
  const frazione = Math.min(rimborsatoCentesimi / totaleCentesimi, 1);
  return -arrotondaCentesimi(provvigioniDellaFattura * frazione);
}

// ============================================================================
// REGIME FISCALE E LIQUIDAZIONE
// ============================================================================

/**
 * Il trattamento fiscale e previdenziale di un partner, come DATO. Tutto facoltativo: un regime senza IVA, senza
 * ritenuta e senza cassa è valido (un forfettario, un partner estero).
 */
export interface RegimeFiscaleDati {
  nome: string;
  /** Paese ISO a 2 lettere a cui si riferisce. */
  paese: string;
  /** IVA che il partner aggiunge in fattura (es. 22); `null` se non la applica. */
  iva_percentuale: number | null;
  /** Ritenuta d'acconto trattenuta da noi. Italia: 23% sul 50% della provvigione. */
  ritenuta: { aliquota: number; base_percentuale: number } | null;
  /** Cassa previdenziale (es. Enasarco): quota a carico nostro e quota del partner, trattenuta da noi. */
  cassa: { nome: string; quota_nostra: number; quota_partner: number } | null;
}

export interface ConteggioLiquidazione {
  /** Somma dei movimenti del periodo (maturato meno storni). */
  imponibile_centesimi: number;
  iva_centesimi: number;
  ritenuta_centesimi: number;
  cassa_partner_centesimi: number;
  /** Non esce dalla tasca del partner: è il nostro costo in più, da versare alla cassa. */
  cassa_nostra_centesimi: number;
  /** Quanto si bonifica al partner: imponibile + IVA − ritenuta − quota sua della cassa. */
  netto_centesimi: number;
  /** Quanto ci costa in tutto: imponibile + IVA + quota nostra della cassa (la ritenuta e la sua quota le versiamo noi). */
  costo_totale_centesimi: number;
}

/** Il conteggio di una liquidazione. Un imponibile negativo (più storni che maturato) non si paga: si riporta. */
export function contaLiquidazione(imponibileCentesimi: number, regime: RegimeFiscaleDati): ConteggioLiquidazione {
  const imponibile = Math.max(0, imponibileCentesimi);
  const iva = regime.iva_percentuale ? arrotondaCentesimi((imponibile * regime.iva_percentuale) / 100) : 0;
  const ritenuta = regime.ritenuta
    ? arrotondaCentesimi((((imponibile * regime.ritenuta.base_percentuale) / 100) * regime.ritenuta.aliquota) / 100)
    : 0;
  const cassaPartner = regime.cassa ? arrotondaCentesimi((imponibile * regime.cassa.quota_partner) / 100) : 0;
  const cassaNostra = regime.cassa ? arrotondaCentesimi((imponibile * regime.cassa.quota_nostra) / 100) : 0;
  return {
    imponibile_centesimi: imponibile,
    iva_centesimi: iva,
    ritenuta_centesimi: ritenuta,
    cassa_partner_centesimi: cassaPartner,
    cassa_nostra_centesimi: cassaNostra,
    netto_centesimi: imponibile + iva - ritenuta - cassaPartner,
    costo_totale_centesimi: imponibile + iva + cassaNostra,
  };
}

/** Le frequenze ammesse: periodi che dividono l'anno, così ogni periodo sta in un anno solo. */
export const FREQUENZE_DI_LIQUIDAZIONE = [1, 2, 3, 4, 6, 12] as const;
export const FREQUENZA_PREDEFINITA = 3;

export function frequenzaValida(mesi: number): boolean {
  return (FREQUENZE_DI_LIQUIDAZIONE as readonly number[]).includes(mesi);
}

/** Il periodo di liquidazione che contiene `data`: mesi `AAAA-MM`, allineati all'anno solare (trimestri: gen-mar…). */
export function periodoDiLiquidazione(data: string, ogniMesi: number): { dal: string; al: string } {
  if (!frequenzaValida(ogniMesi)) throw new Error(`Frequenza non valida: ${ogniMesi} mesi`);
  msDellaData(data);
  const [anno, mese] = data.split('-').map(Number);
  const primo = Math.floor((mese - 1) / ogniMesi) * ogniMesi + 1;
  const ultimo = primo + ogniMesi - 1;
  const due = (n: number) => String(n).padStart(2, '0');
  return { dal: `${anno}-${due(primo)}`, al: `${anno}-${due(ultimo)}` };
}

// ============================================================================
// REGISTRAZIONE DI UN'AZIENDA
// ============================================================================

/** Per quanti giorni un'azienda registrata resta del partner se non si abbona (Simone, 19/10/2026). */
export const GIORNI_DI_REGISTRAZIONE_PREDEFINITI = 180;

/** La data in cui scade una registrazione fatta il giorno `registrataIl`. */
export function scadenzaRegistrazione(registrataIl: string, giorni: number): string {
  return dataDaMs(msDellaData(registrataIl) + giorni * GIORNO_MS);
}

/**
 * La partita IVA ridotta a una chiave confrontabile: senza spazi né segni, maiuscola, col prefisso del paese
 * (`04009250921` in Italia → `IT04009250921`). Due registrazioni con la stessa chiave sono la stessa azienda.
 */
export function chiavePartitaIva(partitaIva: string, paese: string): string {
  const pulita = partitaIva.replace(/[\s.\-_/]/g, '').toUpperCase();
  if (!pulita) return '';
  return /^[A-Z]{2}/.test(pulita) ? pulita : `${paese.toUpperCase()}${pulita}`;
}
